import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Trophy, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AwardsPanel({ rfpId }) {
  const [awards, setAwards] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get(`/rfps/${rfpId}/recommend-awards`).then(({ data }) => setAwards(data)).catch(() => {});
  }, [rfpId]);

  const run = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/rfps/${rfpId}/recommend-awards`);
      setAwards(data);
      toast.success("AI awards generated");
    } catch { toast.error("Could not generate awards"); }
    finally { setGenerating(false); }
  };

  const recs = awards?.recommendations || [];

  return (
    <div className="tray p-6" data-testid="awards-panel">
      <div className="flex justify-between items-start mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-amber-500" />
          <div>
            <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500">AI Award Recommendations</div>
            <div className="font-display text-2xl mt-0.5">
              {recs.length > 0 ? `${recs.length} winners chosen` : "Not yet generated"}
            </div>
            {awards?.summary && <div className="text-xs text-text-secondary mt-1 max-w-2xl">{awards.summary}</div>}
          </div>
        </div>
        <button onClick={run} disabled={generating} data-testid="run-awards-btn"
          className="btn-tactile inline-flex items-center gap-2 text-xs">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
          {recs.length > 0 ? "Re-run" : "Generate Awards"}
        </button>
      </div>

      {recs.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {recs.map((r, i) => (
            <div key={i} className="tray-inset p-4" data-testid={`award-${r.category?.toLowerCase().replace(' ', '-')}`}>
              <div className="flex justify-between items-baseline mb-2">
                <span className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">{r.category}</span>
                <span className="font-display text-xl text-amber-500">{r.score}</span>
              </div>
              <div className="font-display text-lg">{r.winner_company}</div>
              {r.runner_up_company && r.runner_up_company !== r.winner_company && (
                <div className="font-mono-data text-[10px] text-text-secondary mt-0.5 inline-flex items-center gap-1">
                  <ArrowRight className="w-3 h-3" /> Runner-up: {r.runner_up_company}
                </div>
              )}
              <div className="text-xs text-text-secondary mt-2 leading-relaxed">{r.rationale}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
